// Uploads local changes to Supabase through PostgREST, so Row Level Security is
// enforced on every write. Downloads come from PowerSync (POWERSYNC.md §4).
//
// SQLite has no booleans, arrays or JSON types, so the local rows carry 0/1,
// JSON strings, and JSON-array strings. Postgres columns are typed, so the
// connector converts known columns before sending.
import { type AbstractPowerSyncDatabase, type PowerSyncBackendConnector, UpdateType } from '@powersync/web';
import { getSupabase } from '../supabase';
import { env } from '../env';

// Errors that will never succeed on retry; discard the op instead of blocking the queue.
// Postgres: bad input, not-null, FK, unique, check, RLS, unknown column/table.
// PostgREST: unknown column (PGRST204), unknown function (PGRST202), schema cache (PGRST200).
const FATAL = new Set(['22P02', '23502', '23503', '23505', '23514', '42501', '42703', '42P01', 'PGRST204', 'PGRST202', 'PGRST200']);

const BOOLEAN_COLUMNS: Record<string, string[]> = {
  devices: ['is_token_issuer'],
  places: ['active'], icd10_codes: ['is_common', 'active'], dosage_phrases: ['active'], drug_catalog: ['active'],
  patients: ['age_approximate', 'consent_messaging'],
  patient_phones: ['is_primary', 'viber'], patient_allergies: ['active'], patient_conditions: ['active'],
  doctors: ['active'], session_templates: ['active'], tokens: ['priority'],
  price_items: ['doctor_share_applies', 'active'], encounter_diagnoses: ['is_primary'], templates: ['active'],
  drugs: ['is_consumable', 'is_controlled', 'active'], suppliers: ['active'], prescription_items: ['buy_outside'],
  documents: ['include_diagnosis'],
};
const JSONB_COLUMNS: Record<string, string[]> = {
  clinics: ['settings'], drug_catalog: ['pack_units'], drugs: ['pack_units'], templates: ['body'],
  cash_closes: ['expected', 'counted', 'variance'], user_keys: ['kdf'], encounter_versions: ['snapshot'],
};
const ARRAY_COLUMNS: Record<string, string[]> = { prescription_items: ['instruction_codes'] };

export function toPostgresRow(table: string, data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };
  for (const c of BOOLEAN_COLUMNS[table] ?? []) if (c in out && out[c] !== null && out[c] !== undefined) out[c] = out[c] === 1 || out[c] === true || out[c] === '1';
  for (const c of JSONB_COLUMNS[table] ?? []) if (typeof out[c] === 'string') { try { out[c] = JSON.parse(out[c] as string); } catch { /* leave as string */ } }
  for (const c of ARRAY_COLUMNS[table] ?? []) if (typeof out[c] === 'string') { try { const v = JSON.parse(out[c] as string); if (Array.isArray(v)) out[c] = v; } catch { /* leave */ } }
  return out;
}

export class SupabaseConnector implements PowerSyncBackendConnector {
  async fetchCredentials() {
    const { data } = await getSupabase().auth.getSession();
    if (!data.session) return null;
    return { endpoint: env.powersyncUrl, token: data.session.access_token };
  }

  async uploadData(db: AbstractPowerSyncDatabase) {
    const tx = await db.getNextCrudTransaction();
    if (!tx) return;
    const sb = getSupabase();
    let last: { table: string; id: string; op: string } | null = null;
    try {
      for (const op of tx.crud) {
        last = { table: op.table, id: op.id, op: op.op };
        const table = sb.from(op.table);
        const row = toPostgresRow(op.table, op.opData ?? {});
        let result;
        if (op.op === UpdateType.PUT) result = await table.upsert({ ...row, id: op.id });
        else if (op.op === UpdateType.PATCH) result = await table.update(row).eq('id', op.id);
        else result = await table.delete().eq('id', op.id);
        if (result.error) throw Object.assign(new Error(result.error.message), { code: result.error.code, details: result.error.details });
      }
      await tx.complete();
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code && FATAL.has(code)) {
        console.error(`[upload] discarding unrecoverable change ${last?.op} ${last?.table}/${last?.id}: ${code} ${(e as Error).message}`);
        await tx.complete();
        return;
      }
      console.warn('[upload] will retry', last, e);
      throw e;
    }
  }
}
